import React, { useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import _ from "lodash";
import Block from "../../../../../components/Tailwind/Block";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../components/Form";
import overrideClasses from "../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import {
  Checkpoint,
  CheckpointType,
} from "../../../../../lib/ground-aws-graphql-core/models/Api/Inspection";
import CheckpointTypeComponentRating from "../../../../../components/CheckpointTypeRating";
import { ActionTypes } from "../../../../../utils/types";
import HeaderRound from "../../../../../components/Tailwind/Block/HeaderRound";

interface Props {
  match: Match<{ cid: string; id: string }>;
  intl: IntlShape;
  edition: boolean;
  disabled: boolean;
}

const RoundForm = (props: Props): JSX.Element => {
  const { intl, match, disabled } = props;

  const [loading, setLoading] = useState<boolean>(false);

  const getRound = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.getRound
  );

  const setRound = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setRound
  );

  const round = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.round
  );

  const checkpointTypes = _.uniqBy(
    round?.ratings?.map(r => r.checkpoint?.type),
    "id"
  );

  const checkpoints: {
    checkpointType: CheckpointType;
    checkpoint: Checkpoint;
    action: ActionTypes;
  }[] = [...new Set(round?.ratings?.map(r => r.checkpoint) || [])].map(c => {
    return {
      checkpointType: c.type,
      checkpoint: c,
      action: ActionTypes.TO_KEEP,
    };
  });

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "title",
      label: "page.inspection.round.form.label",
      initialValue: round?.form.title,
      required: true,
      translatable: true,
      disabled,
    },
    {
      name: "location",
      label: "page.inspection.round.form.location",
      initialValue: round?.form.location,
      required: true,
      translatable: true,
      disabled,
    },
    {
      name: "assigned_to",
      label: "page.inspection.round.assigned_to",
      initialValue: `${round?.user.first_name} ${round?.user.last_name}`,
      required: true,
      disabled,
    },
  ];

  const fetchData = async () => {
    const promises = [getRound({ id: match.params.id })];
    setLoading(true);
    await Promise.all(promises).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();

    // Clean the event from the state when we unmount this component
    return () => {
      setRound(null);
    };
  }, []);

  return loading || !round ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <HeaderRound
            item={round}
            title="page.inspection.round.get.round"
            className="border-b border-gray-200"
          />

          <DefaultForm fields={formFields} disablePanel={disabled} />
        </Block>

        {checkpointTypes &&
          checkpointTypes &&
          round?.ratings &&
          checkpointTypes.map((checkpointType: CheckpointType) => (
            <CheckpointTypeComponentRating
              key={checkpointType.id}
              item={checkpointType}
              ratings={round?.ratings}
              intl={intl}
              items={checkpoints}
            />
          ))}
      </div>
    </>
  );
};

export default RoundForm;
